import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, Search, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  conclaveApi,
  type ConclaveBoardPerson,
  type ConclaveBoardResponse,
} from '@/lib/conclave-api';
import { connectConclaveWs } from '@/lib/conclave-ws';
import { formatDisplayPhone, phoneTelHref } from '@/lib/phone-utils';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type Session = { id: string; index: number; status: string };

const TABLES_PER_PAGE = 12;

function applyBoardMove(
  board: ConclaveBoardResponse,
  registrationId: string,
  tableNumber: number
): ConclaveBoardResponse | null {
  const person =
    board.tables.flatMap((t) => t.people).find((p) => p.registrationId === registrationId) ??
    board.unassigned.find((p) => p.registrationId === registrationId);
  if (!person) return null;

  const updatedPerson: ConclaveBoardPerson = { ...person, tableNumber };
  const withoutPerson = {
    tables: board.tables.map((t) => ({
      ...t,
      people: t.people.filter((p) => p.registrationId !== registrationId),
    })),
    unassigned: board.unassigned.filter((p) => p.registrationId !== registrationId),
  };

  const targetIdx = withoutPerson.tables.findIndex((t) => t.tableNumber === tableNumber);
  const tables =
    targetIdx >= 0
      ? withoutPerson.tables.map((t, i) =>
          i === targetIdx ? { ...t, people: [...t.people, updatedPerson] } : t
        )
      : [...withoutPerson.tables, { tableNumber, people: [updatedPerson] }].sort(
          (a, b) => a.tableNumber - b.tableNumber
        );

  return { ...board, tables, unassigned: withoutPerson.unassigned };
}

export function ConclaveSeatingBoard({
  eventId,
  sessions,
  onMoved,
}: {
  eventId: string;
  sessions: Session[];
  onMoved?: () => void;
}) {
  const [sessionId, setSessionId] = useState(() => {
    const active = sessions.find((s) => s.status === 'ACTIVE');
    return active?.id || sessions[0]?.id || '';
  });
  const [board, setBoard] = useState<ConclaveBoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConclaveBoardPerson | null>(null);
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());
  const [moveTo, setMoveTo] = useState<string>('');
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const loadSeq = useRef(0);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const pendingRef = useRef(pendingIds);
  const queuedReloadRef = useRef(false);

  async function loadBoard(sid: string, options?: { silent?: boolean }) {
    const seq = ++loadSeq.current;
    if (!options?.silent) setPage(0);
    if (!sid) {
      setBoard(null);
      setLoading(false);
      return;
    }
    if (!options?.silent) setLoading(true);
    try {
      const data = await conclaveApi.getSeatingBoard(eventId, sid);
      if (seq !== loadSeq.current) return;
      if (options?.silent && pendingRef.current.size > 0) {
        queuedReloadRef.current = true;
        return;
      }
      setBoard(data);
      setDetail((current) => {
        if (!current) return current;
        return (
          data.tables
            .flatMap((table) => table.people)
            .find((person) => person.registrationId === current.registrationId) ??
          data.unassigned.find((person) => person.registrationId === current.registrationId) ??
          current
        );
      });
      if (data.session?.id && data.session.id !== sid) setSessionId(data.session.id);
    } catch {
      if (seq !== loadSeq.current) return;
      toast.error(
        options?.silent ? 'Could not refresh the table board' : 'Failed to load seating board'
      );
      if (!options?.silent) setBoard(null);
    } finally {
      if (seq === loadSeq.current && !options?.silent) setLoading(false);
    }
  }

  const loadBoardRef = useRef(loadBoard);
  loadBoardRef.current = loadBoard;

  useEffect(() => {
    if (!sessionId && sessions[0]) setSessionId(sessions[0].id);
  }, [sessions, sessionId]);

  useEffect(() => {
    void loadBoard(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, sessionId]);

  useEffect(() => {
    const conn = connectConclaveWs(eventId, (msg) => {
      const currentSession = sessionIdRef.current;
      if (!currentSession) return;
      const sameSession =
        msg.type !== 'assignment.updated' ||
        typeof msg.sessionId !== 'string' ||
        msg.sessionId === currentSession;
      if (msg.type !== 'assignment.updated' && msg.type !== 'connection.restored') return;
      if (!sameSession) return;
      if (pendingRef.current.size > 0) {
        queuedReloadRef.current = true;
        return;
      }
      void loadBoardRef.current(currentSession, { silent: true });
    });
    return () => conn.close();
  }, [eventId]);

  const allPeople = useMemo(() => {
    if (!board) return [];
    return [...board.tables.flatMap((t) => t.people), ...board.unassigned];
  }, [board]);

  const q = query.trim().toLowerCase();
  const matchesQuery = (p: ConclaveBoardPerson) => {
    if (!q) return true;
    const hay = [
      p.name,
      p.email,
      p.phone,
      p.city,
      p.gives,
      p.asks,
      p.dreamReferral,
      ...p.connectCategories,
      ...p.giveBusinessCategories,
      p.intro?.title,
      p.intro?.body,
      p.tableNumber != null ? `table ${p.tableNumber}` : 'unassigned',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  };

  const filteredTables = useMemo(() => {
    if (!board) return [];
    return board.tables.map((t) => ({
      ...t,
      people: t.people.filter(matchesQuery),
      hiddenCount: t.people.filter((p) => !matchesQuery(p)).length,
    }));
  }, [board, q]);

  const filteredUnassigned = useMemo(
    () => (board?.unassigned || []).filter(matchesQuery),
    [board, q]
  );

  const selected = allPeople.find((p) => p.registrationId === selectedId) || null;
  const tableOptions = board?.tables.map((t) => t.tableNumber) || [];
  const seatsMax = board?.config.seatsMax ?? null;
  const seatedCount = board ? board.tables.reduce((n, t) => n + t.people.length, 0) : 0;
  const unassignedCount = board?.unassigned.length ?? 0;
  const overCapacity = seatsMax
    ? (board?.tables ?? []).filter((t) => t.people.length > seatsMax).map((t) => t.tableNumber)
    : [];
  const duplicateOrgIds = useMemo(() => {
    const ids = new Set<string>();
    for (const table of board?.tables ?? []) {
      const counts = new Map<string, number>();
      for (const p of table.people) {
        const key = p.organization?.trim().toLowerCase();
        if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      for (const p of table.people) {
        const key = p.organization?.trim().toLowerCase();
        if (key && (counts.get(key) ?? 0) > 1) ids.add(p.registrationId);
      }
    }
    return ids;
  }, [board]);
  const matchingTables = q
    ? filteredTables.filter((t) => t.people.length > 0).map((t) => t.tableNumber)
    : [];
  const pagedView = !q && !showAll;
  const pageCount = Math.max(1, Math.ceil(filteredTables.length / TABLES_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const visibleTables = q
    ? filteredTables.filter((t) => t.people.length > 0)
    : pagedView
      ? filteredTables.slice(
          safePage * TABLES_PER_PAGE,
          safePage * TABLES_PER_PAGE + TABLES_PER_PAGE
        )
      : filteredTables;
  function jumpToTable(tableNumber: number) {
    const index = filteredTables.findIndex((t) => t.tableNumber === tableNumber);
    if (pagedView && index >= 0 && Math.floor(index / TABLES_PER_PAGE) !== safePage) {
      setPage(Math.floor(index / TABLES_PER_PAGE));
    }
    requestAnimationFrame(() => {
      document
        .getElementById(`table-${tableNumber}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function movePerson(registrationId: string, tableNumber: number) {
    if (!sessionId || !board) return;
    if (pendingRef.current.has(registrationId)) return;

    const person =
      board.tables.flatMap((t) => t.people).find((p) => p.registrationId === registrationId) ??
      board.unassigned.find((p) => p.registrationId === registrationId);
    if (person?.tableNumber === tableNumber) {
      setSelectedId(null);
      return;
    }

    const previousBoard = board;
    const nextBoard = applyBoardMove(board, registrationId, tableNumber);
    if (!nextBoard) return;

    setBoard(nextBoard);
    setSelectedId(null);
    if (detail?.registrationId === registrationId) {
      setDetail((d) => (d ? { ...d, tableNumber } : d));
    }
    const pending = new Set(pendingRef.current).add(registrationId);
    pendingRef.current = pending;
    setPendingIds(pending);

    void conclaveApi
      .overrideAssignment(eventId, {
        sessionId,
        registrationId,
        tableNumber,
      })
      .then(() => {
        onMoved?.();
      })
      .catch(() => {
        setBoard(previousBoard);
        if (detail?.registrationId === registrationId) {
          const restored =
            previousBoard.tables
              .flatMap((t) => t.people)
              .find((p) => p.registrationId === registrationId) ??
            previousBoard.unassigned.find((p) => p.registrationId === registrationId);
          if (restored) setDetail(restored);
        }
        toast.error('Move failed — reverted');
      })
      .finally(() => {
        const next = new Set(pendingRef.current);
        next.delete(registrationId);
        pendingRef.current = next;
        setPendingIds(next);
        if (queuedReloadRef.current && next.size === 0) {
          queuedReloadRef.current = false;
          void loadBoardRef.current(sessionIdRef.current, { silent: true });
        }
      });
  }

  function onTableClick(tableNumber: number) {
    if (!selectedId) return;
    movePerson(selectedId, tableNumber);
  }

  if (!sessions.length) {
    return (
      <p className="rounded-2xl border border-dashed border-border bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
        Generate seating first to open the table board.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 space-y-3 bg-background/95 pb-2 pt-1 backdrop-blur">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2 sm:w-56">
            <Label>Session</Label>
            <Select value={sessionId} onValueChange={setSessionId}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    Session {s.index + 1} · {s.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="rounded-xl pl-9"
              placeholder="Search people, city, gives…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-muted px-3 py-1">{seatedCount} seated</span>
          <span className="rounded-full bg-muted px-3 py-1">
            {board?.tables.length ?? 0} tables
          </span>
          <span
            className={cn(
              'rounded-full px-3 py-1',
              unassignedCount > 0 ? 'bg-amber-100 text-amber-900' : 'bg-muted'
            )}
          >
            {unassignedCount} unassigned
          </span>
          {overCapacity.length > 0 ? (
            <span className="rounded-full bg-red-100 px-3 py-1 text-red-800">
              {overCapacity.length} over capacity
            </span>
          ) : null}
          {duplicateOrgIds.size > 0 ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-900">
              {duplicateOrgIds.size} share an organisation with a table-mate
            </span>
          ) : null}
          <div className="ml-auto w-40">
            <Select value="" onValueChange={(v) => jumpToTable(Number(v))}>
              <SelectTrigger className="h-8 rounded-lg text-xs">
                <SelectValue placeholder="Go to table" />
              </SelectTrigger>
              <SelectContent>
                {tableOptions.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    Table {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {q ? (
          <p className="text-xs text-muted-foreground">
            {matchingTables.length === 0 && filteredUnassigned.length === 0 ? (
              'No matches.'
            ) : (
              <>
                Matches in{' '}
                {matchingTables.slice(0, 12).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="mr-1 underline underline-offset-2 hover:text-primary"
                    onClick={() => jumpToTable(n)}
                  >
                    Table {n}
                  </button>
                ))}
                {matchingTables.length > 12 ? `and ${matchingTables.length - 12} more ` : ''}
                {filteredUnassigned.length > 0 ? `· ${filteredUnassigned.length} unassigned` : ''}
              </>
            )}
          </p>
        ) : null}
      </div>

      {selected ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span>
            Moving <strong>{selected.name}</strong> — tap a table to place them
          </span>
          <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedId(null)}>
            Cancel
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Tap a person to select, then tap a table to move. Open a person for full profile details.
        </p>
      )}

      {!loading && filteredUnassigned.length > 0 ? (
        <div className="rounded-2xl border border-dashed border-amber-500/40 bg-amber-500/5 p-4">
          <p className="text-sm font-semibold text-amber-900">
            Unassigned ({filteredUnassigned.length})
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {filteredUnassigned.map((p) => (
              <li
                key={p.registrationId}
                className={cn(
                  'flex items-center gap-2 rounded-xl border bg-background px-3 py-2 text-sm',
                  selectedId === p.registrationId ? 'border-primary' : 'border-border'
                )}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() =>
                    setSelectedId((cur) => (cur === p.registrationId ? null : p.registrationId))
                  }
                >
                  <div className="truncate font-medium">{p.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{p.city || p.email}</div>
                </button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-lg"
                  onClick={() => {
                    setDetail(p);
                    setMoveTo(tableOptions[0] ? String(tableOptions[0]) : '1');
                  }}
                >
                  Details
                </Button>
              </li>
            ))}
          </ul>
          {selected && selected.tableNumber == null ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Tap a numbered table above to seat them.
            </p>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 md:gap-4 2xl:grid-cols-3">
          {visibleTables.map((table) => {
            const isDropTarget = Boolean(selectedId);
            const highlightMatch =
              q && table.people.some((p) => p.registrationId === selectedId || matchesQuery(p));
            return (
              <button
                key={table.tableNumber}
                id={`table-${table.tableNumber}`}
                type="button"
                onClick={() => onTableClick(table.tableNumber)}
                className={cn(
                  'scroll-mt-44 rounded-2xl border bg-card p-3 text-left shadow-sm transition md:rounded-3xl md:p-4',
                  isDropTarget && 'border-primary ring-2 ring-primary/20',
                  highlightMatch && q ? 'border-primary/40' : 'border-border',
                  !isDropTarget && 'cursor-default'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-full border-2 border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                      Table
                    </span>
                    <span className="text-lg font-bold leading-none text-primary">
                      {table.tableNumber}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {table.people.length} seated
                      {seatsMax && table.people.length > seatsMax ? (
                        <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-normal text-red-800">
                          over capacity
                        </span>
                      ) : null}
                      {table.hiddenCount > 0 ? (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          (+{table.hiddenCount} hidden by search)
                        </span>
                      ) : null}
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {table.people.length === 0 ? (
                        <li className="text-xs text-muted-foreground">Empty</li>
                      ) : (
                        table.people.map((p) => (
                          <li key={p.registrationId}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedId((cur) =>
                                  cur === p.registrationId ? null : p.registrationId
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedId((cur) =>
                                    cur === p.registrationId ? null : p.registrationId
                                  );
                                }
                              }}
                              className={cn(
                                'flex w-full items-start gap-2 rounded-xl border px-2 py-1.5 text-left text-sm transition hover:bg-muted/60',
                                selectedId === p.registrationId
                                  ? 'border-primary bg-primary/10'
                                  : 'border-transparent bg-muted/30',
                                pendingIds.has(p.registrationId) && 'opacity-80'
                              )}
                            >
                              {pendingIds.has(p.registrationId) ? (
                                <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
                              ) : (
                                <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-1.5 font-medium">
                                  <span className="truncate">{p.name}</span>
                                  {duplicateOrgIds.has(p.registrationId) ? (
                                    <span
                                      title="Shares an organisation with someone else at this table"
                                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                    />
                                  ) : null}
                                </div>
                                <div className="truncate text-[11px] text-muted-foreground">
                                  {[p.organization, p.city, p.gives.split(' | ')[0]]
                                    .filter(Boolean)
                                    .join(' · ') || p.email}
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 shrink-0 px-2 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDetail(p);
                                  setMoveTo(String(p.tableNumber ?? table.tableNumber));
                                }}
                              >
                                Details
                              </Button>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!loading && (pagedView ? pageCount > 1 : filteredTables.length > TABLES_PER_PAGE) ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          {pagedView ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-lg"
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
              >
                Previous
              </Button>
              <span className="text-muted-foreground">
                Page {safePage + 1} of {pageCount}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-lg"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                Next
              </Button>
            </div>
          ) : (
            <span className="text-muted-foreground">
              Showing all {filteredTables.length} tables
            </span>
          )}
          {!q ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show pages' : `Show all ${filteredTables.length} tables`}
            </Button>
          ) : null}
        </div>
      ) : null}

      <Sheet open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {detail ? (
            <>
              <SheetHeader>
                <SheetTitle>{detail.name}</SheetTitle>
                <SheetDescription>
                  {detail.city ? `${detail.city} · ` : ''}
                  {detail.tableNumber != null ? `Table ${detail.tableNumber}` : 'Unassigned'}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4 text-sm">
                <Field label="Email" value={detail.email} />
                <Field
                  label="Phone"
                  value={
                    detail.phone ? (
                      <a
                        href={phoneTelHref(detail.phone)}
                        className="font-medium text-primary hover:underline"
                      >
                        {formatDisplayPhone(detail.phone)}
                      </a>
                    ) : (
                      '—'
                    )
                  }
                />
                <Field label="Gives" value={detail.gives || '—'} />
                <Field label="Asks" value={detail.asks || '—'} />
                <Field label="Dream referral" value={detail.dreamReferral || '—'} />
                <Field label="Seek Connects" value={detail.connectCategories.join(', ') || '—'} />
                <Field
                  label="Your Connects"
                  value={detail.giveBusinessCategories.join(', ') || '—'}
                />
                {detail.intro ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Speaking intro
                    </p>
                    <p className="mt-1 font-medium">{detail.intro.title}</p>
                    <p className="text-muted-foreground">{detail.intro.body}</p>
                  </div>
                ) : null}

                <div className="space-y-2 border-t border-border pt-4">
                  <Label>Move to table</Label>
                  <div className="flex gap-2">
                    <Select value={moveTo} onValueChange={setMoveTo}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Table" />
                      </SelectTrigger>
                      <SelectContent>
                        {(tableOptions.length ? tableOptions : [1, 2, 3, 4, 5]).map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            Table {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      className="rounded-xl"
                      disabled={pendingIds.has(detail.registrationId) || !moveTo}
                      onClick={() => {
                        movePerson(detail.registrationId, Number(moveTo));
                      }}
                    >
                      {pendingIds.has(detail.registrationId) ? 'Saving…' : 'Move'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 whitespace-pre-wrap break-words text-foreground">{value}</div>
    </div>
  );
}
